import { useVerifyPhone } from '@/services'
import { Dialog, Transition } from '@headlessui/react'
import { Dispatch, Fragment, SetStateAction, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import OTPInput from 'react-otp-input'
import { useOnClickOutside } from 'usehooks-ts'
import { ButtonPrimary } from '../button'
import { Close } from '../icons'

export type VerifyPhoneModalProps = {
  open: boolean
  onClose: () => void
  phoneNumber: string
  isoCode: string
  dialCode: string
  setIsValidPhone: Dispatch<SetStateAction<boolean>>
  resendMinutes: number
  resendSeconds: number
  isResendCounting: boolean
  setIsResendCounting: Dispatch<SetStateAction<boolean>>
  onResendClick: () => void
  resetCountDown: () => void
}

export const VerifyPhoneModal = (props: VerifyPhoneModalProps) => {
  const [verifyDigits, setVerifyDigits] = useState<string>('')

  const modalRef = useRef(null)

  const onClickOutsideModal = () => {
    onCloseModal()
  }

  useOnClickOutside(modalRef, onClickOutsideModal)

  const { mutate: verifyPhone } = useVerifyPhone()

  const onCloseModal = () => {
    props.onClose()
    setVerifyDigits('')
  }

  const onClickVerify = () => {
    verifyPhone(
      {
        isoCode: props.isoCode,
        otp: verifyDigits,
        phoneNumber: `${props.dialCode}${props.phoneNumber}`,
      },
      {
        onSuccess: () => {
          setVerifyDigits('')
          props.setIsValidPhone(true)
          props.onClose()
          toast.success('Verified')
          props.setIsResendCounting(false)
          props.resetCountDown()
        },
        onError: (err: any) => {
          setVerifyDigits('')
          toast.error(err.response?.data.message)
        },
      }
    )
  }

  const countDownDisplay = (minutes: number, seconds: number) => {
    return `${minutes < 10 ? `0${minutes}` : minutes}:${seconds < 10 ? `0${seconds}` : seconds}`
  }

  return (
    <Transition appear show={props.open} as={Fragment}>
      <Dialog as="div" className={'relative z-20'} open={props.open} onClose={onCloseModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                ref={modalRef}
                className={
                  'rounded-lg bg-white overflow-hidden transform transition-all p-10 shadow-sm shadow-slate-400 w-full max-w-md relative'
                }
              >
                <div className="absolute top-4 right-4 p-1 cursor-pointer">
                  <Close onClick={onCloseModal} />
                </div>
                <Dialog.Title className={'mb-4 text-xl font-medium text-navy-900'}>
                  Verify Your Mobile Number
                </Dialog.Title>
                <Dialog.Description className={'mb-4 text-sm opacity-70'}>
                  A 4-digit code was sent to your phone number - enter it here to verify it
                </Dialog.Description>

                <form>
                  <div className="flex flex-col gap-y-8">
                    <OTPInput
                      inputStyle="flex-1 max-w border-2 border-primary rounded aspect-square max-w-[64px]"
                      containerStyle="justify-between gap-x-6 xs:gap-x-8 sm:gap-x-10 sm:mx-4"
                      renderInput={(props) => <input {...props} />}
                      numInputs={4}
                      value={verifyDigits}
                      onChange={setVerifyDigits}
                      shouldAutoFocus
                    />

                    <div className="flex flex-row justify-between gap-x-2 sm:gap-x-4">
                      <ButtonPrimary
                        type="button"
                        className="w-1/2 text-md rounded-full px-5 py-3 disabled:opacity-60 bg-gray-500"
                        onClick={props.onResendClick}
                        disabled={props.isResendCounting}
                        title={`Resend ${
                          props.isResendCounting ? countDownDisplay(props.resendMinutes, props.resendSeconds) : ''
                        }`}
                      />
                      <ButtonPrimary
                        type="button"
                        className="outline-none w-1/2 text-md font-medium bg-primary-500 rounded-full text-white px-5 py-3 disabled:opacity-40"
                        disabled={verifyDigits.length !== 4}
                        onClick={onClickVerify}
                        title="Verify"
                      />
                    </div>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
